import { useCallback, useState } from 'react';

/**
 * Der Statusbereich der Werkbank. Der Reiter bestimmt die MENGE, die Filter
 * darunter verfeinern sie — umgekehrt gelesen waere ein Score-Filter
 * statusuebergreifend und niemand koennte sagen, worauf sich die Kopfzahl
 * bezieht.
 */
export type WorkbenchStatus = 'pending' | 'approved' | 'rejected';

/**
 * Der zugehoerige Wert in `products.approval_status`. Bewusst als eigener Typ
 * und nicht als `string`: die Spalte kennt zusaetzlich `NULL` (Altbestand), und
 * ein weiter Rueckgabetyp haette genau das verdeckt, was die Reiter NICHT
 * abdecken.
 */
export type WorkbenchApprovalValue = 'pending_review' | 'approved' | 'rejected';

export const WORKBENCH_STATUSES: readonly WorkbenchStatus[] = [
  'pending',
  'approved',
  'rejected',
] as const;

const APPROVAL_VALUE: Record<WorkbenchStatus, WorkbenchApprovalValue> = {
  pending: 'pending_review',
  approved: 'approved',
  rejected: 'rejected',
};

export function approvalValueFor(status: WorkbenchStatus): WorkbenchApprovalValue {
  return APPROVAL_VALUE[status];
}

/** Praefix des localStorage-Schluessels. Der Feed bildet den Rest. */
export const WORKBENCH_STATUS_STORAGE_PREFIX = 'workbench-status:';

export function statusStorageKey(feedId: string): string {
  return `${WORKBENCH_STATUS_STORAGE_PREFIX}${feedId}`;
}

/**
 * Exportiert seit der URL-Verdrahtung (Spec 5.1): `readStatusFromSearch`
 * braucht denselben Waechter wie `readStatusPreference`, und eine zweite
 * Kopie der drei erlaubten Werte waere die Stelle, an der ein vierter Reiter
 * nur in einem der beiden Pfade auftaucht.
 */
export function isWorkbenchStatus(value: unknown): value is WorkbenchStatus {
  return typeof value === 'string' && (WORKBENCH_STATUSES as readonly string[]).includes(value);
}

/**
 * Der Reiter als Deep-Link (Spec 5.1).
 *
 * Liefert `null` bei fehlendem oder ungueltigem Wert — das ist bewusst KEIN
 * Ruecksprung auf `'pending'`: ein ungueltiger Parameter soll die bestehende
 * Praeferenz (localStorage) unangetastet lassen, nicht sie ueberschreiben.
 * Nur ein GUELTIGER Parameter gewinnt (Spec 5.1: "Ein gueltiger Parameter
 * gewinnt ueber localStorage").
 */
export function readStatusFromSearch(params: URLSearchParams): WorkbenchStatus | null {
  const raw = params.get('status');
  return isWorkbenchStatus(raw) ? raw : null;
}

/**
 * Der Wert des URL-Parameters `scope`, der den Werkbank-Filter
 * "noch nicht bei Google" setzt (Spec 5.2). Eigener Name statt eines
 * dritten Reiter-Werts: der Filter ist keine Menge wie `status`, sondern
 * eine Verfeinerung, die auf allen drei Reitern gilt.
 */
export const AWAITING_UPLOAD_SCOPE = 'awaiting_upload';

/**
 * Traegt `scope=awaiting_upload` diese URL? Anders als der Reiter ist das
 * KEINE Praeferenz (kein localStorage) — der Filter gilt nur, solange der
 * Parameter in der URL steht, und verschwindet mit ihm.
 */
export function hasAwaitingUploadScope(params: URLSearchParams): boolean {
  return params.get('scope') === AWAITING_UPLOAD_SCOPE;
}

/**
 * Liest den gemerkten Reiter eines Feeds.
 *
 * Pro Feed und nicht global, aus demselben Grund wie die Ansichtswahl: der
 * Reiter haengt an der Menge, die vor einem liegt. Wer einen Feed komplett
 * freigegeben hat und dort im Reiter „Freigegeben" arbeitet, will beim
 * naechsten Feed trotzdem bei der offenen Arbeit landen.
 *
 * Faellt auf `'pending'` zurueck, wenn nichts gespeichert ist, der Wert kein
 * gueltiger Reiter ist (alte Version, von Hand veraendert) oder der Speicher
 * ueberhaupt nicht lesbar ist. Letzteres ist kein Randfall: im Safari-
 * Privatmodus und bei voller Quote wirft `localStorage`, und eine Reiterwahl
 * darf niemals die Seite reissen.
 */
export function readStatusPreference(feedId: string | null): WorkbenchStatus {
  if (!feedId) return 'pending';
  try {
    const stored = window.localStorage.getItem(statusStorageKey(feedId));
    return isWorkbenchStatus(stored) ? stored : 'pending';
  } catch {
    // Safari-Privatmodus oder volle Quote: die Voreinstellung ist immer sicher.
    return 'pending';
  }
}

/** Gegenstueck zu `readStatusPreference`. Schluckt Speicherfehler bewusst. */
export function writeStatusPreference(feedId: string | null, status: WorkbenchStatus): void {
  if (!feedId) return;
  try {
    window.localStorage.setItem(statusStorageKey(feedId), status);
  } catch {
    // Nicht speichern zu koennen ist kein Fehler, der den Nutzer erreichen muss.
  }
}

/**
 * Aktiver Reiter mit Gedaechtnis pro Feed.
 *
 * Der Feedwechsel wird WAEHREND des Renders nachgezogen, nicht per Effect —
 * dasselbe Muster wie `useViewModePreference` und der Puffer-Reset in
 * `useWorkbench.ts`. Ein Effect liesse einen Frame zu, in dem `feedId` schon
 * der neue Feed ist, der Reiter aber noch der des alten; die Werkbank wuerde
 * in diesem Frame die falsche Produktmenge anfragen und sie sofort wieder
 * wegwerfen.
 */
export function useStatusPreference(
  feedId: string | null,
  /**
   * Der Reiter aus der Adresse, sofern einer dort steht.
   *
   * Er wird NUR fuer den Anfangswert gelesen, und genau das ist sein Zweck:
   * ohne ihn stand im ersten Render die gemerkte Praeferenz, ein Effect zog den
   * Reiter danach nach, und die Werkbank fragte ihre Produktmenge ZWEIMAL an
   * (Infinite-Query plus Gesamtzahl, beide mit Score-Join) -- auf dem
   * Hauptpfad dieses Features, dem Klick auf einen Fortschritts-Chip.
   *
   * Spaetere Aenderungen der Adresse laufen weiterhin ueber den Effect im
   * Aufrufer; dieser Wert aendert daran nichts, weil der Anfangswert nur einmal
   * gelesen wird.
   */
  urlStatus?: WorkbenchStatus | null,
): [WorkbenchStatus, (status: WorkbenchStatus) => void] {
  const [state, setState] = useState<{ feedId: string | null; status: WorkbenchStatus }>(() => ({
    feedId,
    status: urlStatus ?? readStatusPreference(feedId),
  }));

  if (state.feedId !== feedId) {
    setState({ feedId, status: readStatusPreference(feedId) });
  }

  const setStatus = useCallback(
    (status: WorkbenchStatus) => {
      setState({ feedId, status });
      writeStatusPreference(feedId, status);
    },
    [feedId],
  );

  return [state.status, setStatus];
}

/**
 * WP5 A2O-02: die Validierungsregeln, nach denen die Werkbank per URL filtern
 * kann. Genau die drei, deren Befund im Feed-Check auf "In der Werkbank
 * pruefen/kuerzen" verweist; die Reparatur der uebrigen Regeln (GTIN, Bilder)
 * liegt im Shop und nicht in der Werkbank.
 */
export const WORKBENCH_RULE_IDS = ['brand_prefix_mismatch', 'title_too_long', 'norm_claim_ungrounded'] as const;
export type WorkbenchRuleId = (typeof WORKBENCH_RULE_IDS)[number];

/** Name des URL-Parameters. Wie `scope` keine Praeferenz: er gilt nur, solange er in der Adresse steht. */
export const RULE_PARAM = 'rule';

export function readRuleFromSearch(params: URLSearchParams): WorkbenchRuleId | null {
  const raw = params.get(RULE_PARAM);
  return typeof raw === 'string' && (WORKBENCH_RULE_IDS as readonly string[]).includes(raw)
    ? (raw as WorkbenchRuleId)
    : null;
}

/**
 * Das jsonb-Praedikat fuer eine Regel. `validation_warnings` ist ein Array von
 * `{rule_id, severity, message, field}`; `@>` mit einem Ein-Element-Array trifft
 * jedes Produkt, das mindestens einen Eintrag dieser Regel traegt.
 *
 * Liegt hier und nicht in `useWorkbench.ts` (Ruling D13): eine spaetere Stelle
 * (`ruleWorkbenchTarget.ts`), die dasselbe Praedikat braucht, soll es ziehen
 * koennen, ohne den schweren Hook-Import von `useWorkbench.ts` mitzuschleppen.
 * `useWorkbench.ts` importiert die Funktion von hier und reicht sie unter
 * ihrem Namen weiter, damit bestehende Testimporte aus `'./useWorkbench'`
 * unveraendert funktionieren.
 */
export function ruleContainsFilter(ruleId: WorkbenchRuleId): Array<{ rule_id: string }> {
  return [{ rule_id: ruleId }];
}
